import type { PillIntent } from '@/_components';
import type { IconType } from '@/_types/icons';
import {
  ReviewAndExportColumnType,
  ExportColumnType,
  ReviewAndExportFilterObject,
} from '@/_types/reviewAndExport';

export interface ReviewAndExportStatus {
  icon: IconType;
  intent: PillIntent;
  text: string;
  value: string;
}

export const REVIEW_AND_EXPORT_TABLE_HEADER_MAP: Record<
  ReviewAndExportColumnType,
  string
> = {
  select: 'Select',
  name: 'Entity',
  action_required_documents_count: 'Documents to Review',
  export_status: 'Export Status',
  assigned_advisor_id: 'Assigned Firm User',
  actions: 'Actions',
};

export const DEFAULT_REVIEW_AND_EXPORT_FILTER: ReviewAndExportFilterObject = {
  assigned_advisor_id: -1,
  export_filter: 'all',
  all_exported: false,
  review_required_remaining: false,
  failed_analysis_filter: 'all',
  hidden_clients: false,
  new_documents: false,
  has_documents_requiring_review: false,
  selected_entity_tags_filter: [],
};

export const REVIEW_AND_EXPORT_PREORDERED_FORMS = [
  'Summary of Income and Expenses',
  'Corporation Details',
  'Partnership Details',
  'W-2',
  'W-2G',
  '1099-NEC',
  '1099-MISC',
  '1099-OID',
  '1099-K',
  '1099-DIV',
  '1099-INT',
  'Brokerage Statement',
  '1065 K-1',
  '1120S K-1',
  '1041 K-1',
  '1099-R',
  '1098',
  '1098-E',
  '1098-T',
  '1099-Q',
  '1099-G',
  'SSA-1099',
];

export const EXPORT_TABLE_HEADER_MAP: Record<ExportColumnType, string> = {
  select: 'Select',
  document_name: 'Document Name',
  status: 'Status',
  reviewer_name: 'Reviewed By',
  uploaded_at: 'Uploaded On',
  scheduled_on: 'Scheduled On',
  scheduled_by: 'Scheduled By',
  actions: 'Actions',
};

export const EXPORT_STATUS_CONFIGS: {
  id: number;
  text: string;
  value: string;
  icon: IconType;
  intent: PillIntent;
}[] = [
  {
    id: 0,
    text: 'Processing',
    value: 'processing',
    icon: 'circle',
    intent: 'info',
  },
  {
    id: 1,
    text: 'Ready to be Exported',
    value: 'action_required',
    icon: 'export',
    intent: 'warning',
  },
  {
    id: 2,
    text: 'Scheduled',
    value: 'scheduled',
    icon: 'clock',
    intent: 'info',
  },
  {
    id: 3,
    text: 'In Progress',
    value: 'in_progress',
    icon: 'spinner-gap',
    intent: 'info',
  },
  {
    id: 4,
    text: 'Exported',
    value: 'completed',
    icon: 'check-circle',
    intent: 'success',
  },
  {
    id: 5,
    text: 'Failed',
    value: 'failed',
    icon: 'warning-diamond',
    intent: 'danger',
  },
  {
    id: 6,
    text: 'Not exportable',
    value: 'not_exportable',
    icon: 'prohibit',
    intent: 'default',
  },
  {
    id: 7,
    text: 'In Progress',
    value: 'in_qa',
    icon: 'spinner-gap',
    intent: 'info',
  },

  // For internal use only
  {
    id: 8,
    text: 'In Progress',
    value: 'system_failure',
    icon: 'spinner-gap',
    intent: 'info',
  },

  {
    id: 9,
    text: 'Already E-filed',
    value: 'already_e_filed',
    icon: 'prohibit',
    intent: 'default',
  },
];

export function getExportStatusConfig(status: string) {
  return EXPORT_STATUS_CONFIGS.find((item) => item.value === status);
}
